import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSreInvestigationMode,
  isSreInvestigationAvailable,
} from '../constants';
import {
  hasCompletedInvestigation,
  latestCompletedInvestigation,
  latestInvestigation,
  listInvestigations,
} from '../services/sreInvestigationService';
import type { StoredInvestigationRun } from '../types';
import {
  DEFAULT_HISTORY_POLL_MS,
  HISTORY_REFRESH_DEBOUNCE_MS,
  publishHistorySync,
  subscribeHistorySync,
} from '../utils/historyLiveSync';

export interface UseTicketInvestigationsOptions {
  /** Override poll interval (ms). Set 0 to disable polling. */
  pollIntervalMs?: number;
}

export interface UseTicketInvestigationsResult {
  runs: StoredInvestigationRun[];
  /** True only during the first load (no cached runs yet). Background sync is silent. */
  loading: boolean;
  error: string | null;
  investigated: boolean;
  latest: StoredInvestigationRun | null;
  latestCompleted: StoredInvestigationRun | null;
  refresh: () => void;
  /** Notify other tabs/clients that history changed for this ticket. */
  notifyHistoryChanged: () => void;
}

/**
 * Loads persisted SRE runs for a ticket from pulse-sre (via Pulse BFF when live).
 * T-035: polls while mounted, refreshes on tab focus, and syncs across tabs via BroadcastChannel.
 *
 * Background refreshes (poll / focus / other-tab sync) are silent so the ticket CTA
 * does not stick on "Checking…" when multiple detail tabs are open.
 */
export function useTicketInvestigations(
  ticketNumber: string | undefined | null,
  options: UseTicketInvestigationsOptions = {}
): UseTicketInvestigationsResult {
  const [runs, setRuns] = useState<StoredInvestigationRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const hasLoadedRef = useRef(false);
  const inFlightRef = useRef(false);
  const debounceTimerRef = useRef<number | null>(null);
  const fetchGenRef = useRef(0);
  const loadedTicketRef = useRef<string | null>(null);

  const pollIntervalMs =
    options.pollIntervalMs == null
      ? DEFAULT_HISTORY_POLL_MS
      : Math.max(0, options.pollIntervalMs);

  const refresh = useCallback(() => {
    // Coalesce rapid focus/poll/broadcast triggers so overlapping aborts
    // cannot leave the CTA stuck on the initial loading state.
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      // Skip scheduling another fetch while one is already in flight — the
      // in-flight result is fresh enough; next poll/focus will pick up later.
      if (inFlightRef.current) return;
      setRefreshKey((k) => k + 1);
    }, HISTORY_REFRESH_DEBOUNCE_MS);
  }, []);

  const notifyHistoryChanged = useCallback(() => {
    const number = ticketNumber?.trim();
    if (!number) return;
    publishHistorySync(number);
    // Force a local refresh even if a poll is in flight.
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setRefreshKey((k) => k + 1);
  }, [ticketNumber]);

  useEffect(() => {
    const number = ticketNumber?.trim() || null;
    if (loadedTicketRef.current !== number) {
      loadedTicketRef.current = number;
      hasLoadedRef.current = false;
    }

    if (!number || !isSreInvestigationAvailable()) {
      hasLoadedRef.current = false;
      setRuns([]);
      setError(null);
      setLoading(false);
      return;
    }

    // Demo mode has no live history store
    if (getSreInvestigationMode() !== 'live') {
      hasLoadedRef.current = false;
      setRuns([]);
      setError(null);
      setLoading(false);
      return;
    }

    const gen = ++fetchGenRef.current;
    const controller = new AbortController();
    inFlightRef.current = true;

    // Only the first load shows "Checking…". Background sync keeps the last
    // known CTA (Investigated / Start) so multi-tab polling cannot hide it.
    if (!hasLoadedRef.current) {
      setLoading(true);
    }
    setError(null);

    void listInvestigations(
      { ticketNumber: number, limit: 50 },
      controller.signal
    )
      .then((rows) => {
        if (controller.signal.aborted || gen !== fetchGenRef.current) return;
        setRuns(rows);
        hasLoadedRef.current = true;
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || gen !== fetchGenRef.current) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load investigations';
        setError(message);
        // Keep prior runs on soft-refresh failure so the CTA does not flicker.
        if (!hasLoadedRef.current) {
          setRuns([]);
        }
      })
      .finally(() => {
        if (gen !== fetchGenRef.current) return;
        inFlightRef.current = false;
        setLoading(false);
      });

    return () => {
      controller.abort();
      if (gen === fetchGenRef.current) {
        inFlightRef.current = false;
      }
    };
  }, [ticketNumber, refreshKey]);

  // Cross-tab BroadcastChannel sync (T-035) — other tabs only; silent refresh.
  useEffect(() => {
    const number = ticketNumber?.trim();
    if (!number || getSreInvestigationMode() !== 'live') return;
    return subscribeHistorySync(number, refresh);
  }, [ticketNumber, refresh]);

  // Poll + visibility / focus refresh so other clients stay current (T-035)
  useEffect(() => {
    const number = ticketNumber?.trim();
    if (!number || getSreInvestigationMode() !== 'live') return;

    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      refresh();
    };

    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);

    let intervalId: number | undefined;
    if (pollIntervalMs > 0) {
      intervalId = window.setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          return;
        }
        refresh();
      }, pollIntervalMs);
    }

    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [ticketNumber, pollIntervalMs, refresh]);

  return {
    runs,
    loading,
    error,
    investigated: hasCompletedInvestigation(runs),
    latest: latestInvestigation(runs),
    latestCompleted: latestCompletedInvestigation(runs),
    refresh,
    notifyHistoryChanged,
  };
}
